import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ArrowLeft, ExternalLink, FileText, UserPlus } from 'lucide-react';
import { migrationV1V2Api } from './api/migration-v1-v2';
import type { AutoCatchUpSchoolDetail } from './types';
import { formatDateTime, formatLotSummary, relativeTime } from './components/autoCatchUpPresentation';

/**
 * Historique du rattrapage automatique pour un dossier : chaque passe l'ayant
 * touché, les lots rejoués et le nom des personnes créées. C'est la vue croisée
 * de {@link AutoCatchUpSweepPage} — même journal, lu par école.
 */

function Metric({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className="rounded-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-3">
      <p className={cn('text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100', tone)}>
        {value}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
}

const runStatusLabel: Record<string, { label: string; className: string }> = {
  completed: {
    label: 'Terminée',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  partial: {
    label: 'Avec incidents',
    className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  },
  failed: {
    label: 'En échec',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
};

export function AutoCatchUpSchoolPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [detail, setDetail] = useState<AutoCatchUpSchoolDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useBreadcrumbLabel(
    detail?.school ? `${detail.school.code} — ${detail.school.name}` : null
  );

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      setDetail(await migrationV1V2Api.getAutoCatchUpSchool(Number(projectId)));
      setError(null);
    } catch {
      setError("Historique indisponible : dossier introuvable ou accès refusé.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Link to="/migration-v1-v2/rattrapage?tab=schools">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour
          </Button>
        </Link>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error ?? 'Historique introuvable.'}
        </div>
      </div>
    );
  }

  const { school, summary, runs, eligible } = detail;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="space-y-1">
          <Link to="/migration-v1-v2/rattrapage?tab=schools">
            <Button variant="ghost" size="sm" className="-ml-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Rattrapage par école
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {school?.name ?? `Dossier #${detail.project.id}`}
            </h1>
            {school && (
              <Badge variant="outline" className="font-mono text-xs">
                {school.code}
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Ajouts automatiques d'utilisateurs depuis la bascule en V2
            {!eligible && ' — ce dossier n\'est plus éligible au rattrapage automatique'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/migration-v1-v2/${detail.project.id}`}>
            <Button variant="outline" size="sm">
              <ExternalLink className="h-4 w-4 mr-1" />
              Dossier
            </Button>
          </Link>
          <Link to={`/migration-v1-v2/${detail.project.id}/report`}>
            <Button variant="outline" size="sm">
              <FileText className="h-4 w-4 mr-1" />
              Rapport
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric
          label="Éléments migrés"
          value={summary.added}
          tone={summary.added > 0 ? 'text-sky-600 dark:text-sky-300' : undefined}
        />
        <Metric
          label="Fiches en échec"
          value={summary.failed}
          tone={summary.failed > 0 ? 'text-red-600 dark:text-red-400' : undefined}
        />
        <Metric label="Passes ayant produit un effet" value={summary.runs} />
        <Metric label="Dernier ajout" value={relativeTime(summary.lastRunAt)} />
      </div>

      {runs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 dark:border-slate-700 p-8 text-center text-sm text-slate-500">
          Aucun ajout automatique sur ce dossier — la V1 n'a rien produit de nouveau depuis la
          bascule.
        </div>
      ) : (
        <div className="space-y-4">
          {runs.map((run) => {
            const status = runStatusLabel[run.status] ?? runStatusLabel.completed;
            const lots = (run.lots ?? []).filter(
              (l) =>
                l.added > 0 ||
                (l.reassigned ?? 0) > 0 ||
                (l.preserved ?? 0) > 0 ||
                (l.attached ?? 0) > 0 ||
                l.failed > 0 ||
                l.error
            );
            const relinked = run.relinkedRecords ?? 0;
            return (
              <div
                key={run.id}
                className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 p-4 space-y-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {formatDateTime(run.startedAt)}
                    </span>
                    <span className="text-slate-500">
                      {' '}
                      · {run.trigger === 'cron' ? 'tâche programmée' : 'lancé manuellement'}
                      {run.triggeredBy &&
                        ` par ${run.triggeredBy.firstname} ${run.triggeredBy.lastname}`}
                    </span>
                    {run.sweepId && (
                      <Link
                        to={`/migration-v1-v2/rattrapage/${run.sweepId}`}
                        className="ml-2 text-xs text-blue-600 hover:underline dark:text-blue-400"
                      >
                        voir la passe #{run.sweepId}
                      </Link>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span
                      className={cn(
                        'rounded-md px-2 py-0.5 text-xs font-medium',
                        status.className
                      )}
                    >
                      {status.label}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-md bg-sky-100 px-2 py-0.5 text-xs font-medium tabular-nums text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                      <UserPlus className="h-3 w-3" />
                      {run.addedRecords} migré{run.addedRecords > 1 ? 's' : ''}
                    </span>
                    {relinked > 0 && (
                      <span className="rounded-md bg-violet-100 px-2 py-0.5 text-xs font-medium tabular-nums text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                        {relinked} rattachement{relinked > 1 ? 's' : ''} modifié{relinked > 1 ? 's' : ''}
                      </span>
                    )}
                    {run.failedRecords > 0 && (
                      <span className="rounded-md bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
                        {run.failedRecords} en échec
                      </span>
                    )}
                  </div>
                </div>

                {run.error && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
                    {run.error}
                  </div>
                )}

                {run.additions.length > 0 && (
                  <div className="overflow-x-auto rounded-lg border dark:border-slate-700">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50 dark:bg-slate-800/60 text-gray-500">
                          <th className="text-left py-2 px-3 font-medium">Élément migré</th>
                          <th className="text-left py-2 px-3 font-medium">Lot</th>
                          <th className="text-left py-2 px-3 font-medium">Résultat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {run.additions.map((addition, i) => (
                          <tr
                            key={`${addition.lotCode}-${addition.sourceId}-${i}`}
                            className="border-b last:border-b-0 dark:border-slate-700"
                          >
                            <td className="py-2 px-3 font-medium text-gray-800 dark:text-gray-200">
                              {addition.label}
                            </td>
                            <td className="py-2 px-3 text-slate-500 whitespace-nowrap">
                              {addition.lotName}
                            </td>
                            <td className="py-2 px-3">
                              {addition.status !== 'migrated' ? (
                                <span className="text-red-700 dark:text-red-300">
                                  Échec{addition.error ? ` — ${addition.error}` : ''}
                                </span>
                              ) : addition.kind === 'reassigned' ? (
                                <span className="text-violet-700 dark:text-violet-300">
                                  Changé de classe{addition.change ? ` — ${addition.change}` : ''}
                                </span>
                              ) : addition.kind === 'preserved' ? (
                                <span className="text-orange-700 dark:text-orange-300">
                                  Affectation V2 conservée, à arbitrer{addition.change ? ` — ${addition.change}` : ''}
                                </span>
                              ) : addition.kind === 'attached' ? (
                                <span className="text-green-700 dark:text-green-300">Rattaché</span>
                              ) : (
                                <span className="text-green-700 dark:text-green-300">Migré</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {run.additionsTruncated && (
                  <p className="text-xs text-gray-400">
                    Liste limitée aux {run.additions.length} premiers éléments — les compteurs
                    restent exacts.
                  </p>
                )}

                {lots.length > 0 && (
                  <p className="text-xs text-slate-500">
                    Lots rejoués : {lots.map(formatLotSummary).join(', ')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
